import React from "react";
import ReactMarkdown from "react-markdown";
import {connect} from "react-redux";
import {
    getTranslate,
    TranslateFunction,
} from "react-localize-redux";

import {Collapse} from "antd";
import {RightOutlined} from "@ant-design/icons";
import StoreState from "../../interfaces/store/storeState";
import Spinner from "../common/Spinner/Spinner";
import "./FaqList.scss";
import SearchField from "../common/SearchField/SearchField";
import {SearchFaqAction} from "../../interfaces/store/actions/faq";
import {searchFaq} from "../../store/actions/faq";
import {Faq} from "../../interfaces/store/actions/payload/faq";
import {FaqTypeAll} from "../../utils/constants";

interface OwnProps {
    isMobile: boolean
}

interface StateProps {
    translate: TranslateFunction
    isLoading: boolean
    list: Faq[]
    searchValue: string
    activeFaqType: string
}

interface DispatchProps {
    searchFaq(payload: string): SearchFaqAction
}

type Props = StateProps & DispatchProps & OwnProps;

class FaqList extends React.Component<Props> {
    private static readonly translationPath: string = "components.FaqList.FaqList";

    /**
     *  Depending on current active faq type we either return whole list or we return filtered part of it
     * @param {Faq[]} list
     * @param {string} activeFaqType
     * @returns {Faq[]}
     */
    static getFilteredList(list: Faq[], activeFaqType: string):Faq[] {
        let filteredList: Faq[] = [];
        if (activeFaqType === FaqTypeAll) {
            filteredList = list;
        } else {
            list.forEach((faq: Faq) => {
                if (faq.Category?.trim().replace(/\s/g, "-") === activeFaqType) {
                    filteredList.push(faq);
                }
            });
        }
        return filteredList;
    }

    constructor(props: Props) {
        super(props);

        this.onSearchHandler = this.onSearchHandler.bind(this);

        this.getFaqList = this.getFaqList.bind(this);
    }

    /**
     * Search faqs handler, fires faq search action
     * @param {string} value
     */
    onSearchHandler(value: string): void {
        this.props.searchFaq(value);
    }

    /**
     * Based on filtered category list all items within collapse component
     * @param {Faq[]} list
     * @returns React.ReactNode
     */
    getFaqList(list: Faq[]): React.ReactNode {
        const {translate}:Props = this.props;
        const searchValue: string = this.props.searchValue.toLowerCase();
        let filteredList: Faq[] = list;
        if (searchValue) {
            filteredList = list.filter((faq: Faq): boolean => faq.Question.toLowerCase().includes(searchValue));
        }
        return (
            <Collapse
                className="df-faq-collapse"
                expandIcon={(panelProps): React.ReactNode => (
                    <RightOutlined
                        rotate={panelProps.isActive ? 90 : 0}
                        aria-label={
                            translate(`${FaqList.translationPath}.${panelProps.isActive ? "hideAnswer" : "showAnswer"}.text`) as string
                        }
                    />
                )}
            >
                {filteredList.map((faq: Faq, index: number): React.ReactNode => {
                    const {Answer, Category, Question}: Faq = faq;
                    return (
                        <Collapse.Panel header={Question} key={`${Category}-${index}`}>
                            <ReactMarkdown linkTarget="_blank">{Answer}</ReactMarkdown>
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        );
    }

    render() {
        const {
            isLoading, translate, list, activeFaqType,
        }: Props = this.props;

        const filteredList: Faq[] = FaqList.getFilteredList(list, activeFaqType);
        return (
            <>
                {isLoading && (<Spinner size="large"/>)}
                <div className="df-job-list-search">
                    <SearchField
                        onSearch={this.onSearchHandler}
                        inputProps={{
                            placeholder: translate(`${FaqList.translationPath}.searchFaqs.text`) as string,
                            "aria-label": translate(`${FaqList.translationPath}.searchFaqs.text`) as string,
                        }}
                    />
                </div>
                <div className="df-faq-list-container">
                    {!!filteredList.length && this.getFaqList(filteredList)}
                </div>
            </>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        isLoading: state.mask.isLoading,
        list: state.faq.list,
        activeFaqType: state.faq.activeFaqType,
        searchValue: state.faq.searchValue,
    };
}

const mapDispatchToProps: DispatchProps = {
    searchFaq,
};

export default connect(mapStateToProps, mapDispatchToProps)(FaqList);
